<template>
  <div v-if="open" class="qr-overlay" @click.self="close">
    <div class="qr-dialog">
      <div class="qr-header">
        <h6 class="mb-0 font-weight-bold text-white"><i class="fe fe-camera mr-2"></i> Scan enrollee QR code</h6>
        <button type="button" class="close text-white" aria-label="Close" @click="close"><span aria-hidden="true">&times;</span></button>
      </div>

      <div class="qr-body">
        <div v-if="error" class="alert alert-warning mb-0">
          <i class="fe fe-alert-triangle mr-1"></i> {{ error }}
        </div>
        <div v-else class="qr-frame">
          <video ref="video" playsinline muted autoplay></video>
          <div class="qr-target"></div>
          <div v-if="starting" class="qr-status">Starting camera…</div>
        </div>
        <canvas ref="canvas" class="d-none"></canvas>
        <p v-if="!error" class="text-muted text-center mt-3 mb-0" style="font-size: 0.85rem;">
          Point the camera at the QR code on the enrollee's ID card or slip.
        </p>
        <p v-if="notice" class="text-danger text-center mt-2 mb-0" style="font-size: 0.85rem;">{{ notice }}</p>
      </div>

      <div class="qr-footer">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="close">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import jsQR from "jsqr";

// Enrollee QR codes (EnrolleeSlip, ID cards) hold https://app.claimify.ng/verify-user/<users.id>; export links
// /verify-user/client/<users.id> or /verify-user/dependant/<dependants.id>.
const VERIFY_URL = /\/verify-user\/(?:(client|dependant)\/)?(\d+)/;

export default {
  name: "QrScannerModal",
  props: { open: { type: Boolean, default: false } },

  data() {
    return { stream: null, frame: null, starting: false, error: "", notice: "" };
  },

  watch: {
    open(val) {
      if (val) this.$nextTick(this.start);
      else this.stop();
    },
  },

  beforeDestroy() {
    this.stop();
  },

  methods: {
    async start() {
      this.error = "";
      this.notice = "";
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.error = "This browser cannot open the camera. Use Chrome or Safari on a secure (https) page.";
        return;
      }
      this.starting = true;
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (!this.open) return this.stop();
        const video = this.$refs.video;
        video.srcObject = this.stream;
        await video.play();
        this.starting = false;
        this.scan();
      } catch (e) {
        this.starting = false;
        this.error = e && e.name === "NotAllowedError"
          ? "Camera access was refused. Allow the camera for this site in the browser settings and try again."
          : "The camera could not be opened" + (e && e.message ? `: ${e.message}` : ".");
      }
    },

    scan() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      if (!this.stream || !video || !canvas) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA && video.videoWidth) {
        // Downscale large camera frames: faster and still enough for a printed code.
        const scale = Math.min(1, 640 / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(image.data, image.width, image.height, { inversionAttempts: "dontInvert" });
        if (code && code.data) {
          const match = code.data.match(VERIFY_URL);
          if (match) {
            this.stop();
            this.$emit("scanned", { type: match[1] || "client", id: match[2] });
            return;
          }
          this.notice = "This QR code is not a Claimify enrollee code.";
        }
      }
      this.frame = requestAnimationFrame(this.scan);
    },

    stop() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      if (this.stream) this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
      this.starting = false;
    },

    close() {
      this.stop();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.qr-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.qr-dialog {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
.qr-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}
.qr-body {
  padding: 18px;
}
.qr-footer {
  padding: 12px 18px;
  border-top: 1px solid #e9ecef;
  text-align: right;
}
.qr-frame {
  position: relative;
  background: #0f172a;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 1 / 1;
}
.qr-frame video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.qr-target {
  position: absolute;
  inset: 18%;
  border: 3px solid rgba(255, 255, 255, 0.85);
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.35);
  pointer-events: none;
}
.qr-status {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.9rem;
}
</style>
